import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { Box, Button, Chip, Popover, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import type { ChannelOption } from '../format';
import type { FilterOption } from '../hooks/useFilterOptions';
import { COLORS } from '../theme';

const chipSx = (active: boolean) =>
  ({
    height: 28,
    fontWeight: active ? 600 : 400,
    fontSize: '0.78rem',
    bgcolor: active ? COLORS.primary : 'transparent',
    color: active ? '#fff' : COLORS.text,
    border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
    borderRadius: '6px',
    '& .MuiChip-label': { px: 1.25 },
    '&:hover': { bgcolor: active ? COLORS.primaryHover : COLORS.pageBg },
  }) as const;

const sectionLabelSx = {
  color: COLORS.muted,
  fontWeight: 700,
  fontSize: '0.7rem',
  letterSpacing: 0.5,
  minWidth: 52,
  flexShrink: 0,
  alignSelf: 'center',
} as const;

interface FilterChipProps {
  label: string;
  active: boolean;
  disabled: boolean;
  disabledReason: string;
  onClick: () => void;
}

// MUI's `disabled` styling sets `pointer-events: none` on the Chip root itself
// (see Chip.js), which also blocks the hover/focus events a Tooltip needs to
// fire — a disabled chip's explanation silently never rendered (Lucas, PR
// #4642 review). Wrapping the Chip in a <span> gives the Tooltip something
// that still receives pointer events to attach its listeners to; this is
// MUI's own documented fix for tooltips on disabled elements.
const FilterChip = ({ label, active, disabled, disabledReason, onClick }: FilterChipProps) => (
  <Tooltip title={disabled ? disabledReason : ''} placement='top' arrow>
    <span>
      <Chip
        label={label}
        size='small'
        clickable={!disabled}
        disabled={disabled}
        // MUI's `disabled`/`clickable` props style the chip but don't gate
        // `onClick` itself -- it fires regardless, since it's a plain React
        // handler underneath (verified live: a disabled chip toggled active
        // and emptied the report). Guard here too.
        onClick={() => !disabled && onClick()}
        // AC1 (#4627, review Lucas): disabled chips must read as struck
        // through, not just MUI's default disabled opacity.
        sx={{
          ...chipSx(active),
          ...(disabled && { '& .MuiChip-label': { px: 1.25, textDecoration: 'line-through' } }),
        }}
      />
    </span>
  </Tooltip>
);

interface IProps {
  dspOptions: readonly FilterOption[];
  dspLoading: boolean;
  channelOptions: ChannelOption[];
  channelLoading: boolean;
  canViewPlatform: boolean;
  selectedDsps: string[];
  selectedChannels: string[];
  /** Platforms the current Channel selection still allows, or null for no restriction (flank#4627). */
  allowedDsps: Set<string> | null;
  /** Channels the current Platform selection still allows, or null for no restriction (flank#4627). */
  allowedChannels: Set<string> | null;
  /** Non-null only when the Channel row is actually narrowed by a Platform selection. */
  channelNote: string | null;
  /** Non-null only when the Platform row is actually narrowed by a Channel selection. */
  platformNote: string | null;
  activeCount: number;
  onToggleDsp: (value: string) => void;
  onToggleChannel: (value: string) => void;
}

/**
 * Platform and Channel chips, collapsed by default behind one button (#4626)
 * instead of two always-visible rows — the chip lists themselves (fetching,
 * the orphan-keeping `channelOptions` memo, permission gating, and the
 * DSP<->Channel capability narrowing, #4627) still live in
 * ReportingV2FilterBar.tsx, which owns the data; this only renders them.
 */
const PlatformChannelFilter = ({
  dspOptions,
  dspLoading,
  channelOptions,
  channelLoading,
  canViewPlatform,
  selectedDsps,
  selectedChannels,
  allowedDsps,
  allowedChannels,
  channelNote,
  platformNote,
  activeCount,
  onToggleDsp,
  onToggleChannel,
}: IProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Button
        variant='outlined'
        disableElevation
        onClick={event => setAnchorEl(event.currentTarget)}
        endIcon={<KeyboardArrowDownRounded sx={{ color: COLORS.muted }} />}
        sx={{
          justifyContent: 'space-between',
          textTransform: 'none',
          minWidth: 130,
          px: 1.5,
          py: 1.1,
          borderRadius: '10px',
          bgcolor: '#fff',
          color: COLORS.text,
          borderColor: activeCount > 0 ? COLORS.primaryBorder : COLORS.border,
          '&:hover': { bgcolor: '#fff', borderColor: activeCount > 0 ? COLORS.primaryBorder : COLORS.muted },
        }}
      >
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Typography component='span' variant='body2' sx={{ fontWeight: 500 }}>
            Platform & Channel
          </Typography>
          {activeCount > 0 && (
            <Typography component='span' variant='body2' sx={{ color: COLORS.primary, fontWeight: 600 }}>
              · {activeCount}
            </Typography>
          )}
        </Box>
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 340,
              p: 1.5,
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            },
          },
        }}
      >
        {/* Platform row — #4442/#4623: not offered at all without Platform permission */}
        {canViewPlatform && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Typography variant='overline' sx={sectionLabelSx}>
              PLATFORM
            </Typography>
            {dspOptions.length === 0 && !dspLoading && (
              <Typography variant='caption' sx={{ color: COLORS.muted }}>
                No platforms available.
              </Typography>
            )}
            {dspOptions.map(dsp => {
              const active = selectedDsps.includes(dsp.value);
              const disabled = allowedDsps !== null && !allowedDsps.has(dsp.value);
              return (
                <FilterChip
                  key={dsp.value}
                  label={dsp.label}
                  active={active}
                  disabled={disabled}
                  disabledReason='Not supported by the selected channel(s).'
                  onClick={() => onToggleDsp(dsp.value)}
                />
              );
            })}
          </Box>
        )}
        {canViewPlatform && platformNote && (
          <Typography variant='caption' sx={{ color: COLORS.muted, pl: `${sectionLabelSx.minWidth}px` }}>
            {platformNote}
          </Typography>
        )}

        {/* Channel row — options come from the caller's own delivery (#4627) */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
          <Typography variant='overline' sx={sectionLabelSx}>
            Channel
          </Typography>
          {channelOptions.length === 0 && !channelLoading && (
            <Typography variant='caption' sx={{ color: COLORS.muted }}>
              No channels reported for this selection.
            </Typography>
          )}
          {channelOptions.map(opt => {
            // opt.values is more than one only for a restricted viewer's
            // merged option (flank#4741 Problem 2) — match/gate on any raw
            // value it represents, not the (possibly synthetic) opt.value.
            const active = opt.values.some(v => selectedChannels.includes(v));
            const disabled = allowedChannels !== null && !opt.values.some(v => allowedChannels.has(v));
            return (
              <FilterChip
                key={opt.value}
                label={opt.label}
                active={active}
                disabled={disabled}
                disabledReason='Not supported by the selected platform(s).'
                onClick={() => onToggleChannel(opt.value)}
              />
            );
          })}
        </Box>
        {channelNote && (
          <Typography variant='caption' sx={{ color: COLORS.muted, pl: `${sectionLabelSx.minWidth}px` }}>
            {channelNote}
          </Typography>
        )}
      </Popover>
    </>
  );
};

export default PlatformChannelFilter;
